<template>
  <div ref="controlBar" class="control-bar">
    <div>
      <label class="control-label" for="measurement-select">Measurements</label>
      <select
        class="control-select"
        id="measurement-select"
        v-model="calculatorKey"
      >
        <optgroup
          v-for="measurementGroup in MEASUREMENT_GROUPS"
          :label="measurementGroup.groupName"
        >
          <option
            v-for="measurement in measurementGroup.measurements"
            :key="measurement.key"
            :value="measurement.key"
            :disabled="CALCULATORS_DISABLED_ON_LATEST.has(measurement.key) && periodKey === 'latest'"
          >{{ measurement.name }}</option>
        </optgroup>
      </select>
    </div>
    <div>
      <label class="control-label" for="period-select">Color By</label>
      <select
        class="control-select"
        id="period-select"
        v-model="periodKey"
      >
        <option value='latest' :disabled="(calculator instanceof PercentagePerformanceCalculator) || (calculator instanceof UpDaysDownDaysCalculator)" >Latest Value</option>
        <optgroup
          v-for="periodGroup in PERIOD_GROUPS"
          :label="periodGroup.groupName"
        >
          <option
            v-for="period in periodGroup.measurements"
            :key="period.key"
            :value="period.key"
          >{{ period.name }}</option>
        </optgroup>
      </select>
    </div>
    <div>
      <label class="control-label" for="size-by-select">Size By</label>
      <select
        class="control-select"
        id="size-by-select"
        v-model="sizeByKey"
      >
        <option
          v-for="sizeBy in SIZE_BY_OPTIONS"
          :key="sizeBy.key"
          :value="sizeBy.key"
        >{{ sizeBy.name }}</option>
      </select>
    </div>
    <div>
      <label class="control-label" for="color-scheme-select">Color Scheme</label>
      <select
        class="control-select"
        id="color-scheme-select"
        v-model="colorSchemeKey"
      >
        <optgroup
          v-for="colorGroup in COLOR_GROUPS"
          :label="colorGroup.groupName"
        >
          <option
            v-for="colorScheme in colorGroup.colorSchemes"
            :key="colorScheme.key"
            :value="colorScheme.key"
          >{{ colorScheme.name }}</option>
        </optgroup>
      </select>
    </div>
    <div class="checkbox-group-container">
      <div class="checkbox-container">
        <label class="checkbox-label">
          <input type="checkbox" v-model="isAutoRefreshEnabled">
          <span class="checkbox-indicator"></span>
          <span>Auto-Refresh</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" v-model="showSymbols">
          <span class="checkbox-indicator"></span>
          <span>Show Symbols</span>
        </label>
      </div>
      <div class="checkbox-container">
        <label class="checkbox-label">
          <input type="checkbox" v-model="isHeaderShown">
          <span class="checkbox-indicator"></span>
          <span>Info Box on Hover</span>
        </label>
        <label class="checkbox-label">
          <input type="checkbox" v-model="isSharpChartShown">
          <span class="checkbox-indicator"></span>
          <span>Mini Chart on Hover</span>
        </label>
      </div>
    </div>
    <div class="btn-row">
      <button
        v-if="isFullScreenSupported"
        type="button"
        class="control-btn"
        :class="{ 'control-btn-active': isFullScreen }"
        @click="toggleFullScreen()"
      >
        <i
          class="fas fa-fw"
          :class="{
            'fa-expand': !isFullScreen,
            'fa-compress': isFullScreen
          }"
          aria-hidden="true"
        ></i>
      </button>
      <button
        v-show="controlBarWidth > 800"
        type="button"
        class="control-btn"
        :class="{ 'control-btn-active': showSideTable }"
        @click="toggleSideTable()"
      >
        <i class="far fa-fw fa-window-maximize fa-rotate-90" aria-hidden="true"></i>
        <span v-show="controlBarWidth > 991" class="btn-label">&nbsp;Table</span>
      </button>
      <button
        v-if="showShareButton"
        type="button"
        class="control-btn control-btn-share"
        @click="isShareModalOpen = true"
      >
        <i class="fas fa-fw fa-share" aria-hidden="true"></i>
        <span v-show="controlBarWidth > 991" class="btn-label">&nbsp;Share</span>
      </button>
    </div>
    <ShareModal
      v-if="showShareButton"
      :is-open="isShareModalOpen"
      @close="isShareModalOpen = false"
    />
  </div>
</template>

<script lang="ts" setup>
  import { COLOR_GROUPS } from '@/ts/constants/colorSchemes';
  import { MEASUREMENT_GROUPS } from '@/ts/constants/measurements';
  import { PERIOD_GROUPS } from '@/ts/constants/periods';
  import { SIZE_BY_OPTIONS } from '@/ts/constants/sizeBy';
  import { PercentagePerformanceCalculator, UpDaysDownDaysCalculator } from '@/ts/map/calculationFunctions';
  import { ref } from 'vue';
  import { useMarketCarpetContext } from '@/ts/marketCarpetContext';
  import { useBoundingClientRect } from '@/ts/composables/useBoundingClientRect';
  import ShareModal from '@/components/ShareModal.vue';
  import type { CalculatorKey } from '@/ts/composables/useCalculator';

  const CALCULATORS_DISABLED_ON_LATEST = new Set<CalculatorKey>(['performance', 'updown']);

  defineProps<{
    showShareButton?: boolean;
  }>();

  const controlBar = ref<HTMLElement>();
  const { width: controlBarWidth } = useBoundingClientRect(controlBar, { windowResize: true });

  const isShareModalOpen = ref(false);

  const {
    colorSchemeKey,
    calculatorKey,
    calculator,
    periodKey,
    sizeByKey,
    isHeaderShown,
    isSharpChartShown,
    showSymbols,
    isAutoRefreshEnabled,
    showSideTable,
    toggleSideTable,
    isFullScreen,
    isFullScreenSupported,
    toggleFullScreen,
  } = useMarketCarpetContext();
</script>

<style scoped>
  .control-bar {
    background-color: var(--component-white-color);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin: 0px;
    padding: 10px;
    gap: 15px;
    font-family: 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    box-sizing: border-box;
  }

  .control-label {
    display: block;
    font-size: 10px;
    text-transform: uppercase;
    color: #4f5964;
    margin-bottom: 2px;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: bold;
    line-height: 1.0;
    padding: 0 0 0 5px;
  }

  .control-select {
    height: 30px;
    padding: 5px 20px 5px 8px;
    font-size: 12px;
    max-width: 140px;
    width: auto;
    display: inline-block;
    vertical-align: middle;
    border: 2px solid #c0ced8;
    border-radius: 5px;
    background: #fff url(//d.stockcharts.com/img/menu-icon.png) no-repeat right 6px center;
    background-size: 10px 12px;
    line-height: 1.5;
    color: #707f8f;
    outline: none;
    box-shadow: none;
    cursor: pointer;
    box-sizing: border-box;
    font-weight: bold;
    font-family: 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    text-overflow: ellipsis;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    transition: all 0.35s ease;
  }

  .control-select:hover {
    border-color: #707f8f;
    color: #2d3339;
  }

  .control-select:active,
  .control-select:focus {
    outline: none;
    box-shadow: none;
    border-color: #c0ced8;
    color: #707f8f;
  }

  .checkbox-group-container {
    display: flex;
    gap: 10px;
  }

  .checkbox-container {
    padding-top: 5px;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 12px;
    font-weight: bold;
    color: var(--background-color);
    white-space: nowrap;
    height: 18px;
    margin: 0px;
    padding: 0px;
    line-height: 1.4;
    gap: 5px;
    font-family: 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }

  .checkbox-label input[type="checkbox"] {
    position: absolute;
    opacity: 0.0;
    width: 0px;
    height: 0px;
    pointer-events: none;
  }

  .checkbox-indicator {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 10px;
    height: 10px;
    border: 2px solid #0f4d8a;
    border-radius: 50%;
    background-color: transparent;
    flex-shrink: 0;
    transition: background-color 0.15s, border-color 0.15s;
  }

  .checkbox-label:hover {
    color: #2d3339;
  }

  .checkbox-label input[type="checkbox"]:checked + .checkbox-indicator {
    background-color: #0f4d8a;
    border-color: #0f4d8a;
  }

  .checkbox-label input[type="checkbox"]:checked + .checkbox-indicator::after {
    content: '';
    display: block;
    width: 2.5px;
    height: 5px;
    border: solid #fff;
    border-width: 0px 1.5px 1.5px 0px;
    transform: rotate(45deg);
    margin-top: -1px;
  }

  .btn-row {
    display: flex;
    justify-content: right;
    gap: 10px;
    margin: 0px;
    flex: 1;
  }

  .control-btn {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 4px 8px;
    font-size: 12px;
    line-height: 1.5;
    font-weight: bold;
    font-family: 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    border: 2px solid #707f8f;
    border-radius: 3px;
    background: transparent;
    color: #707f8f;
    cursor: pointer;
    white-space: nowrap;
    outline: none;
    box-shadow: none;
    box-sizing: border-box;
    transition: all 0.35s ease;
  }

  .control-btn:hover,
  .control-btn:active,
  .control-btn:focus {
    background: #5a6672;
    border-color: #5a6672;
    color: #fff;
    text-decoration: none;
  }

  .control-btn-active {
    background: #434c56;
    border-color: #434c56;
    color: #fff;
  }

  .control-btn-active:hover,
  .control-btn-active:active,
  .control-btn-active:focus {
    background: #5a6672;
    border-color: #5a6672;
    color: #fff;
  }

  .control-btn-share {
    background: #0f4d8a;
    border-color: #0f4d8a;
    color: #fff;
  }

  .control-btn-share:hover,
  .control-btn-share:active,
  .control-btn-share:focus {
    background: #2a73bb;
    border-color: #2a73bb;
    color: #fff;
  }
</style>
